import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    CellUpdate,
    CollabFlowObject,
    MemoriesModelQuery,
    RawCell,
    Tool,
    WishedTool
} from "../../models/collaborate.model";
import { ControlPanelComponentType } from "../../types/tools";

export type NodeState = {
    collabApp: {
        id: string;
        title: string;
        description: string;
        status: string;
        // persona: string;
        createdAt: number;
        updatedAt: number;
        json_structure: {
            watermark: string;
            app: object;
            file: object;
            display?: {
                tools: {
                    title: string;
                    description: string;
                    arguments: string[];
                }[];
            };
            activators_required?: string[];
        };
        type?: "rigid" | "malleable";
    } | null;
    selectedMemories: any[];
    memories: any[];
    selectedTools: any[];
    tools: Tool[];
    neuralPathWays: any[];
    cells: RawCell[];
    // selectedCellIndex: number;
    selectedCellId: number;
    selectedToolIdentifier: number;
    mutatedNodes: any[];
    flows: CollabFlowObject[];
    // rawFlows: any[],
    isAllMemoriesSelected: boolean;
    controlPanelStatus: "warning" | "normal" | "hidden";
    selectedControlPanelComponent: ControlPanelComponentType;
    segregatedMemories: MemoriesModelQuery[];
    thirdPartyMemories: MemoriesModelQuery[];
    useRLHFSearch: boolean;
    shouldSearchNotes: boolean;
    selectedWishedToolId: number;
    selectedAgencyToolIndex: number;
};

const initialState: NodeState = {
    collabApp: null,
    selectedMemories: [],
    memories: [],
    selectedTools: [],
    tools: [],
    neuralPathWays: [],
    cells: [],
    mutatedNodes: [],
    flows: [],
    // rawFlows: [],
    controlPanelStatus: "hidden",
    selectedControlPanelComponent: "none",
    // selectedCellIndex: -1,
    selectedCellId: -1,
    selectedToolIdentifier: -1,
    isAllMemoriesSelected: true,
    segregatedMemories: [],
    thirdPartyMemories: [],
    useRLHFSearch: false,
    shouldSearchNotes: false,
    selectedWishedToolId: -1,
    selectedAgencyToolIndex: -1
};

const nodeSlice = createSlice({
    name: "nodeData",
    initialState,
    reducers: {
        setCollaborationApp: (state, action: PayloadAction<any>) => {
            state.collabApp = action.payload;
        },
        setSelectedMemories: (state, action: PayloadAction<any[]>) => {
            state.selectedMemories = action.payload;
        },
        setMemories: (state, action: PayloadAction<any[]>) => {
            state.memories = action.payload;
        },
        setSelectedTools: (state, action: PayloadAction<Tool[]>) => {
            state.selectedTools = action.payload;
        },
        setTools: (state, action: PayloadAction<Tool[]>) => {
            state.tools = action.payload;
        },
        setNeuralPathWays: (state, action: PayloadAction<any[]>) => {
            state.neuralPathWays = action.payload;
        },
        setCells: (state, action: PayloadAction<any[]>) => {
            state.cells = action.payload;
            state.selectedCellId = -1;
            state.selectedToolIdentifier = -1;
            state.controlPanelStatus = "hidden";
            state.selectedControlPanelComponent = "none";
        },
        updateCell: (state, action: PayloadAction<CellUpdate>) => {
            state.cells = state.cells.map((cell) => {
                if (cell.unique_identifier === action.payload.unique_identifier) {
                    return {
                        ...cell,
                        ...action.payload
                    };
                }
                return cell;
            });
        },
        addCell: (state, action: PayloadAction<any>) => {
            const cells = [...state.cells];
            cells.push(action.payload);
            state.cells = cells;
        },
        setMutatedNodes: (state, action: PayloadAction<any[]>) => {
            state.mutatedNodes = action.payload;
        },
        setFlows: (state, action: PayloadAction<any[]>) => {
            state.flows = action.payload;
        },
        setControlPanelStatus: (state, action: PayloadAction<"warning" | "normal" | "hidden">) => {
            state.controlPanelStatus = action.payload;
        },
        setSelectedControlPanelComponent: (
            state,
            action: PayloadAction<ControlPanelComponentType>
        ) => {
            state.selectedControlPanelComponent = action.payload;
        },
        setSelectedCellId: (state, action: PayloadAction<number>) => {
            state.selectedCellId = action.payload;
        },
        setSelectedToolIdentifier: (state, action: PayloadAction<number>) => {
            state.selectedToolIdentifier = action.payload;
        },
        setIsAllMemoriesSelected: (state, action: PayloadAction<boolean>) => {
            state.isAllMemoriesSelected = action.payload;
        },
        setSegregatedMemories: (state, action: PayloadAction<MemoriesModelQuery[]>) => {
            state.segregatedMemories = action.payload;
        },
        setThirdPartyMemories: (state, action: PayloadAction<MemoriesModelQuery[]>) => {
            state.thirdPartyMemories = action.payload;
        },
        checkSegregatedMemory(state, action: PayloadAction<{ id: string; checked: boolean }>) {
            state.segregatedMemories = state.segregatedMemories.map((memory) => {
                if (memory.id === action.payload.id) {
                    memory.isSelected = action.payload.checked;
                }
                return memory;
            });
        },
        checkThirdPartyMemory(state, action: PayloadAction<{ id: string; checked: boolean }>) {
            state.thirdPartyMemories = state.thirdPartyMemories.map((memory) => {
                if (memory.id === action.payload.id) {
                    memory.isSelected = action.payload.checked;
                }
                return memory;
            });
        },
        checkAllSegregatedMemories(state) {
            state.segregatedMemories = state.segregatedMemories.map((memory) => {
                memory.isSelected = true;
                return memory;
            });
        },
        uncheckAllSegregatedMemories(state) {
            state.segregatedMemories = state.segregatedMemories.map((memory) => {
                memory.isSelected = false;
                return memory;
            });

            state.isAllMemoriesSelected = false;
        },
        setUseRLHFSearch: (state, action: PayloadAction<boolean>) => {
            state.useRLHFSearch = action.payload;
        },
        setShouldSearchNotes: (state, action: PayloadAction<boolean>) => {
            state.shouldSearchNotes = action.payload;
        },
        /**
         * State methods for tools
         */
        setSelectedWishedToolId: (state, action: PayloadAction<number>) => {
            state.selectedWishedToolId = action.payload;
        },
        updateSelectedCellToolWishList: (state, action: PayloadAction<Partial<WishedTool>>) => {
            state.cells = state.cells.map((cell) => {
                if (cell.unique_identifier === state.selectedCellId) {
                    const newToolWishList = cell.tool_wishlist.map((wishedTool) => {
                        if (wishedTool.tool_identifier === state.selectedWishedToolId) {
                            if (action.payload.selected_tool) {
                                action.payload.selected_tool = {
                                    ...wishedTool.selected_tool,
                                    ...action.payload.selected_tool
                                };
                            }
                            return {
                                ...wishedTool,
                                ...action.payload
                            };
                        }
                        return wishedTool;
                    });

                    return {
                        ...cell,
                        tool_wishlist: newToolWishList
                    };
                }

                return cell;
            });
        },
        /**
         * Agency tools methods
         */
        setSelectedAgencyToolIndex: (state, action: PayloadAction<number>) => {
            state.selectedAgencyToolIndex = action.payload;
        },
        updateRigidCellTool: (state, action: PayloadAction<Partial<WishedTool>>) => {
            if (
                state.selectedCellId &&
                state.selectedCellId > 0 &&
                state.selectedToolIdentifier &&
                state.selectedToolIdentifier > 0
            ) {
                const cellIndex = state.cells.findIndex(
                    (cell) => cell.unique_identifier === state.selectedCellId
                );
                const toolIndex = state.cells[cellIndex]?.tool_wishlist.findIndex(
                    (tool) => tool.tool_identifier === state.selectedToolIdentifier
                );
                if (cellIndex !== -1 && toolIndex !== -1) {
                    state.cells[cellIndex].tool_wishlist[toolIndex] = {
                        ...state.cells[cellIndex].tool_wishlist[toolIndex],
                        selected_tool: {
                            ...state.cells[cellIndex].tool_wishlist[toolIndex].selected_tool,
                            ...action.payload.selected_tool
                        }
                    };
                }
            } else {
                state.cells[0].tool_wishlist[0] = {
                    ...state.cells[0].tool_wishlist[0],
                    selected_tool: {
                        ...state.cells[0].tool_wishlist[0].selected_tool,
                        ...action.payload.selected_tool
                    }
                };
            }
        }
    }
});

export const selectControlPanelStatus = (state: { nodeData: NodeState }) =>
    state.nodeData.controlPanelStatus;
export const selectSelectedControlPanelComponent = (state: { nodeData: NodeState }) =>
    state.nodeData.selectedControlPanelComponent;
export const selectSelectedCell = (state: { nodeData: NodeState }) =>
    state.nodeData.cells.find((cell) => cell.unique_identifier === state.nodeData.selectedCellId);
export const selectSelectedTool = (state: { nodeData: NodeState }) =>
    selectSelectedCell(state)?.tool_wishlist.find(
        (tool) => tool.tool_identifier === state.nodeData.selectedToolIdentifier
    );
export const selectCollabApp = (state: { nodeData: NodeState }) => state.nodeData.collabApp;
export const selectCells = (state: { nodeData: NodeState }) => state.nodeData.cells;
export const selectTools = (state: { nodeData: NodeState }) => state.nodeData.tools;
export const selectFlows = (state: { nodeData: NodeState }) => state.nodeData.flows;
export const selectedMemories = (state: { nodeData: NodeState }) => state.nodeData.selectedMemories;
export const selectIsAllMemoriesSelected = (state: { nodeData: NodeState }) =>
    state.nodeData.isAllMemoriesSelected;
export const selectSegregatedMemories = (state: { nodeData: NodeState }) =>
    state.nodeData.segregatedMemories;
export const selectThirdPartyMemories = (state: { nodeData: NodeState }) =>
    state.nodeData.thirdPartyMemories;

export const {
    setCollaborationApp,
    setCells,
    checkSegregatedMemory,
    checkThirdPartyMemory,
    setFlows,
    setMemories,
    setShouldSearchNotes,
    setUseRLHFSearch,
    setMutatedNodes,
    setNeuralPathWays,
    // setRawFlows,
    setSelectedMemories,
    setSelectedTools,
    setIsAllMemoriesSelected,
    setTools,
    setControlPanelStatus,
    setSelectedControlPanelComponent,
    addCell,
    // setSelectedCellIndex,
    updateCell,
    checkAllSegregatedMemories,
    uncheckAllSegregatedMemories,
    setSegregatedMemories,
    setThirdPartyMemories,
    setSelectedWishedToolId,
    updateSelectedCellToolWishList,
    setSelectedCellId,
    setSelectedToolIdentifier,
    setSelectedAgencyToolIndex,
    updateRigidCellTool
} = nodeSlice.actions;

export default nodeSlice.reducer;
